import { ClassicEditor, Essentials, Bold, Italic, Paragraph, Heading, HeadingButtonsUI, ParagraphButtonUI, Link } from 'ckeditor5';
import { List as ListCK } from 'ckeditor5';
import mfConditionalFields from "mf-conditional-fields";
import List from 'list.js';
import Pristine from 'pristinejs';
import ApexCharts from 'apexcharts';
import axios, { Axios } from 'axios';

// Account-only behaviour modules. Each is a no-op when its markup is absent, because
// this bundle loads on every account layout.
import "./festival-branding.js";


/**
 * Run one block of page setup in isolation.
 *
 * Everything below used to sit directly in a single DOMContentLoaded handler, so a
 * throw in any block aborted every block after it — a dead Google Maps API in the
 * address lookup took out the geocode fallback, form validation and CKEditor with it,
 * and the page just stopped wiring itself up with nothing shown to the user. See #254.
 *
 * console.error rather than Sentry.captureException: app.js inits Sentry with
 * consoleLoggingIntegration at "error", and the bundled SDK does not define
 * window.Sentry.
 */
function section(name, fn) {
    try {
        fn();
    } catch (e) {
        console.error('account.js section failed: ' + name, e);
    }
}


document.addEventListener('DOMContentLoaded', () => {

    //
    // Address lookup
    //
    section('address lookup', function () {
        var formLocation = document.getElementById('form-location');

        if(formLocation) {

            // Activate auto lookup.
            //
            // Caught here as well as by section() above, because the geocode fallback
            // below is set up in the same block and has to survive a dead Maps API — it
            // is the only thing populating lat/lng without the dropdown. See #234.
            var autocompleteReady = false;

            try {
                autocompleteReady = window.addressField !== undefined
                    && window.addressField.activateSubfield("address-street1") !== false;
            } catch (e) {
                // app.js inits Sentry with consoleLoggingIntegration at "error", so this
                // still reaches Sentry — unlike window.Sentry, which the bundled SDK does
                // not define.
                console.error('Address autocomplete failed to start', e);
            }

            // Autocomplete is how lat/lng normally get populated. Without it the form still
            // works — the submit handler below geocodes server-side — but the user needs to
            // know the dropdown isn't coming, or they'll wait for it.
            if(!autocompleteReady) {
                var addressNotice = document.getElementById('address-autocomplete-notice');
                if(addressNotice) {
                    addressNotice.classList.remove('hidden');
                }
            }

            // Fallback to ensure coords are provided even if users don't use the lookup widget
            var addressRaw = document.getElementById('address-raw');
            var locationLat = document.getElementById('address-lat');
            var locationLng = document.getElementById('address-lng');

            // Do the coordinates in the hidden inputs describe the address text on screen?
            //
            // True on load when we already have coordinates, because they were saved
            // together with the address they belong to. Typing in any address field breaks
            // that pairing; a Places selection restores it (address-field.js sets the flag
            // on place_changed). Kept here rather than in address-field.js so it still works
            // when autocomplete never started. See #253.
            window.addressCoordsMatchText = locationLat.value.length > 0 && locationLng.value.length > 0;

            ['address-street1', 'address-city', 'address-zip'].forEach(function(fieldId) {
                var field = document.getElementById(fieldId);
                if(field) {
                    field.addEventListener('input', function() {
                        window.addressCoordsMatchText = false;
                    });
                }
            });

            formLocation.addEventListener('submit', function(e) {

                // Geocode server-side unless the coordinates still describe the address text.
                //
                // On an edit, `raw` and the coordinates are both restored from what was
                // saved, so "raw is populated" says nothing about whether they still match
                // what is on screen — editing the street without touching the dropdown used
                // to save the new address against the old pin. See #253.
                var missingCoords = locationLat.value.length == 0 || locationLng.value.length == 0;

                if(addressRaw.value.length == 0 || missingCoords || !window.addressCoordsMatchText) {

                    e.preventDefault();

                    var addressLine1 = document.getElementById('address-street1');
                    var addressCity = document.getElementById('address-city');
                    var postCode = document.getElementById('address-zip');

                    // Initialize data for the AJAX call
                    var data = {};

                    // Append CSRF Token to outgoing data
                    data[window.csrfTokenName] = window.csrfTokenValue;

                    // Give the lookup everything we have, and keep it in the UK.
                    //
                    // This sent "<street>, <postcode>" with no country restriction. With a
                    // postcode that is enough; with the postcode field blank it is not, and
                    // Google answers with the same street name anywhere in the world.
                    // Measured against the venues that were wrong on production (#337):
                    //
                    //     "25 Water Street,"  -> New York, USA
                    //     "6 Albert Street,"  -> Brisbane, Australia
                    //     "Town centre,"      -> Eagan, Minnesota, USA
                    //
                    // The town is what actually disambiguates — "25 Water Street, Todmorden"
                    // resolves correctly with or without a restriction — so it goes into the
                    // target. `components` is the backstop for when the town is missing too:
                    // on its own it only moves the answer to the wrong town in the right
                    // country (Liverpool, Aberdare, Bournemouth), which the content-health
                    // bounding box still catches.
                    //
                    // Empty parts are dropped so a blank field cannot become ", ," and
                    // confuse the lookup further.
                    var target = [
                        addressLine1 ? addressLine1.value : '',
                        addressCity ? addressCity.value : '',
                        postCode ? postCode.value : ''
                    ].filter(function (part) {
                        return part && part.trim().length > 0;
                    }).join(', ');

                    // An array target: GeocodingHelper::lookup() takes either, and the
                    // Lookup model formats `components` into the Geocoding API query.
                    data['target'] = {
                        address: target,
                        components: 'country:GB'
                    };

                    axios({
                        method: 'POST',
                        data: data,
                        url: '/actions/google-maps/lookup/one',
                    }).then(function(response) {

                            if(response.status == 200 && response.data != null) {

                                var address = response.data.results;

                                if(address != null) {

                                    document.getElementById('address-lat').value = address.lat;
                                    document.getElementById('address-lng').value = address.lng;
                                    document.getElementById('address-formatted').value = address.formatted;
                                    document.getElementById('address-countryCode').value = address.countryCode;
                                    document.getElementById('address-county').value = address.county;
                                    document.getElementById('address-placeId').value = address.placeId;
                                    document.getElementById('address-neighborhood').value = address.neighborhood;
                                    document.getElementById('address-country').value = address.country;
                                    document.getElementById('address-state').value = address.state;
                                    document.getElementById('address-street2').value = address.street2;

                                    // Submit the form
                                    formLocation.submit();

                                } else {
                                    alert("There was an error. Please enter an address in the 'Street Address' field and make a selection from the dropdown");
                                }

                            } else {
                                alert("There was an error. Please enter an address in the 'Street Address' field and make a selection from the dropdown");
                            }

                    }).catch(function (error) {
                        alert("There was an error. Please enter an address in the 'Street Address' field and make a selection from the dropdown");
                    });

                }

            });

        }
    });

    //
    // Location selctor
    //
    section('location selector', function () {
        if(document.getElementById("locations-list")) {

            var options = {
                valueNames: ['id', 'name', 'address'],
                searchDelay: 250,
                pagination: {
                    item: "<li><a class='page flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-aqua hover:bg-gray-100 hover:text-mine-shaft' href='#'></a></li>",
                    innerWindow: 3
                },
                page: 6
            };

            // Show no results if zero matches
            var userList = new List('locations-list', options)
                .on('updated', function(list) {

                    var noResults = document.querySelector(".list-noresults");
                
                    if(list.matchingItems.length == 0) {
                        noResults.classList.remove("hidden");
                    } else {
                        noResults.classList.add("hidden");
                    }
                
                });

            var selectedCard = document.getElementById('location-selected-card');
            var selectedCardContents = document.getElementById('location-selected-card-contents');
        
            // Update our selected label when the location selected changes
            document.body.addEventListener('change', function (e) {
            
                var selected = document.querySelector('input[name="fields[location][]"]:checked');

                if(selected !== null) {

                    // Update the "selected location" card with the actual selected location
                    var selectedContents = document.querySelector(`label[data-id="${selected.value}"]`);
                    selectedCardContents.innerHTML = selectedContents.innerHTML;

                    // Show the selected location preview card
                    selectedCard.classList.remove('hidden');

                    // User has made a selection, so hide the UI
                    document.getElementById('locations-search').classList.add('hidden');
                    document.querySelector('.pagination').classList.add('hidden');

                    // Show the change button
                    changeLocation.classList.remove('hidden');

                    // Scroll user to selected card
                    selectedCardContents.scrollIntoView({block:"center"});

                }
            });

            // Change selected location button
            var changeLocation = document.getElementById('location-selected-change');

            if(changeLocation) {
                changeLocation.addEventListener("click", function(e) {
                    e.preventDefault();

                    // Show the card
                    selectedCard.classList.remove('hidden');

                    // Show the UI to search
                    document.getElementById('locations-search').classList.remove('hidden');
                    document.querySelector('.pagination').classList.remove('hidden');

                    // Hide the button
                    e.target.classList.add('hidden');

                    // Hide the selected location preview card
                    selectedCard.classList.add('hidden');

                    // Focus input to search bar
                    document.getElementById('search').focus();
                
                });
            }

        }
    });

    //
    // Event schedule / date manipualtion
    //
    section('event schedule', function () {
        if(!document.querySelector("#form-step-3")) {
            return;
        }

        // Shared across every repeater on the step, so two rows added to different
        // fields can never collide on a template id.
        var nextRowId = 1;

        // Registry so the inline onclick can find the repeater that owns a row.
        var repeaters = {};

        // One implementation for all three matrix repeaters on this step. They differ
        // only in which field they post to, what a row looks like, and whether the last
        // row may be removed; the "new:" id convention the server expects, the
        // sortOrder inputs and the delete handling are identical. Step 3 used to have
        // one of these and exhibitions needed two more (#177) - three copies of the
        // same twenty lines is how they drift apart.
        //
        // This still duplicates the server-rendered row in
        // templates/account/events/add/3.twig - keep the two in step. They had drifted
        // before: the labels here lacked `block`, so they sat beside their inputs
        // instead of above them, and said "(24hr)" where the rendered row says
        // "(Required)". Added rows looked like a different form.
        function matrixRepeater(config) {
            var container = document.getElementById(config.container);
            var order = document.getElementById(config.order);
            var addButton = document.getElementById(config.addButton);

            // Step 3 renders EITHER the exhibition fields or the ordinary schedule
            // fields, never both, so absent markup is the normal case and must be a
            // no-op - this bundle loads on every layout.
            if(!container || !order || !addButton) {
                return;
            }

            function syncRemoveLinks() {
                var rows = container.querySelectorAll('[data-row-id]');
                // An event with no dates is not an event (#56), so the last remaining
                // row cannot be removed: the control disappears rather than failing on
                // submit. Closures are the exception - having none is perfectly valid.
                var locked = config.keepOne && rows.length <= 1;

                rows.forEach(function(row) {
                    var link = row.querySelector('a[onclick^="return deleteMatrixRow"]');
                    if(link) {
                        link.hidden = locked;
                    }
                });
            }

            function addRow() {
                var rowId = "new:" + (nextRowId++);
                var template = document.createElement('template');
                template.innerHTML = config.rowHtml(rowId);
                container.appendChild(template.content);

                order.insertAdjacentHTML("beforeend",
                    '<input type="hidden" name="fields[' + config.field + '][sortOrder][]" data-sort-id="' + rowId + '" value="' + rowId + '">');

                syncRemoveLinks();
            }

            function removeRow(rowId) {
                if(config.keepOne && container.querySelectorAll('[data-row-id]').length <= 1) {
                    return false;
                }

                var row = container.querySelector('[data-row-id="' + rowId + '"]');
                var sort = order.querySelector('[data-sort-id="' + rowId + '"]');

                if(row) {
                    row.remove();
                }
                if(sort) {
                    sort.remove();
                }

                syncRemoveLinks();

                return false;
            }

            addButton.addEventListener("click", function(e) {
                e.preventDefault();
                addRow();
            });

            // Opening hours are required but start empty on a new exhibition, so
            // without this the organiser faces a required field with no input in it
            // and a button they have to guess at. The ordinary schedule branch gets
            // away with starting empty because it has a radio group to choose first.
            if(config.autoAddFirst && !container.querySelector('[data-row-id]')) {
                addRow();
            }

            syncRemoveLinks();

            repeaters[config.field] = { remove: removeRow };
        }

        // Global so the server-rendered rows can call it from their inline onclick,
        // which is how this has always worked - now with the field name, so one
        // function serves every repeater.
        globalThis.deleteMatrixRow = function(field, id) {
            var repeater = repeaters[field];
            return repeater ? repeater.remove(String(id)) : false;
        }

        // Every input below carries a data-matomo-name that must match its twin in
        // account/events/add/3.twig. The row id is in the name attribute, so without it
        // FormAnalytics files each saved block as a new field, forever (#419).

        // Specific dates - the original repeater.
        matrixRepeater({
            field: 'specificDates',
            container: 'dates-container',
            order: 'dates-order',
            addButton: 'button-add-date',
            keepOne: true,
            rowHtml: function(rowId) {
                return `
                    <div data-row-id="${rowId}">
                        <input type="hidden" name="fields[specificDates][entries][${rowId}][type]" value="specificDatesBlock">
                        <div class="md:flex items-center justify-between gap-10 border border-gray-300 p-5 rounded-lg">
                            <div class="md:flex items-center gap-8 w-full">
                                <div>
                                    <label for="fields-specificDates-entries-${rowId}-specificDate" class="block mb-1 font-semibold text-mine-shaft">Date <span class="italic text-pink font-light">(Required)</span></label>
                                    <input type="date" required id="fields-specificDates-entries-${rowId}-specificDate" class="bg-gray-50 w-44 md:w-34 lg:w-44 border border-gray-300 text-mine-shaft rounded-lg focus:ring-primary-600 focus:border-primary-600 block p-2.5" name="fields[specificDates][entries][${rowId}][fields][date]" data-matomo-name="Date" value="">
                                </div>
                                <div class="mt-4 md:mt-0">
                                    <label for="fields-specificDates-entries-${rowId}-specificStartTime" class="block mb-1 font-semibold text-mine-shaft">Start time <span class="italic text-pink font-light">(Required)</span></label>
                                    <input type="time" required id="fields-specificDates-entries-${rowId}-specificStartTime" class="bg-gray-50 w-32 border border-gray-300 text-mine-shaft rounded-lg focus:ring-primary-600 focus:border-primary-600 p-2.5" name="fields[specificDates][entries][${rowId}][fields][startTime]" data-matomo-name="Start time" value="09:00">
                                </div>
                                <div class="grow mt-4 md:mt-0 md:text-right">
                                    <a href="#" onclick="return deleteMatrixRow('specificDates', '${rowId}')" class="text-sm underline">Remove date</a>
                                </div>
                            </div>
                        </div>
                    </div>`;
            }
        });

        // Exhibition opening hours - one row per group of days sharing the same times.
        matrixRepeater({
            field: 'openingHours',
            container: 'opening-hours-container',
            order: 'opening-hours-order',
            addButton: 'button-add-opening-hours',
            keepOne: true,
            autoAddFirst: true,
            rowHtml: function(rowId) {
                var days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                var checkboxes = days.map(function(day) {
                    var label = day.charAt(0).toUpperCase() + day.slice(1);
                    return `
                        <div class="flex items-center">
                            <input type="checkbox" id="fields-openingHours-entries-${rowId}-${day}" name="fields[openingHours][entries][${rowId}][fields][weeklyDays][]" data-matomo-name="Opening hours - days" value="${day}" class="w-4 h-4 text-pink bg-gray-100 border-gray-300 rounded focus:ring-pink focus:ring-2">
                            <label for="fields-openingHours-entries-${rowId}-${day}" class="ms-2 text-sm font-medium text-mine-shaft cursor-pointer">${label}</label>
                        </div>`;
                }).join('');

                return `
                    <div data-row-id="${rowId}">
                        <input type="hidden" name="fields[openingHours][entries][${rowId}][type]" value="openingHoursBlock">
                        <div class="border border-gray-300 p-5 rounded-lg">
                            <fieldset>
                                <legend class="block mb-1 font-semibold text-mine-shaft">Open on <span class="italic text-pink font-light">(Required)</span></legend>
                                <div class="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-4">${checkboxes}</div>
                            </fieldset>
                            <div class="md:flex items-center gap-8">
                                <div>
                                    <label for="fields-openingHours-entries-${rowId}-openTime" class="block mb-1 font-semibold text-mine-shaft">Opens <span class="italic text-pink font-light">(Required)</span></label>
                                    <input type="time" required id="fields-openingHours-entries-${rowId}-openTime" class="bg-gray-50 w-32 border border-gray-300 text-mine-shaft rounded-lg focus:ring-primary-600 focus:border-primary-600 p-2.5" name="fields[openingHours][entries][${rowId}][fields][startTime]" data-matomo-name="Opening hours - opens" value="10:00">
                                </div>
                                <div class="mt-4 md:mt-0">
                                    <label for="fields-openingHours-entries-${rowId}-closeTime" class="block mb-1 font-semibold text-mine-shaft">Closes <span class="italic text-pink font-light">(Required)</span></label>
                                    <input type="time" required id="fields-openingHours-entries-${rowId}-closeTime" class="bg-gray-50 w-32 border border-gray-300 text-mine-shaft rounded-lg focus:ring-primary-600 focus:border-primary-600 p-2.5" name="fields[openingHours][entries][${rowId}][fields][closeTime]" data-matomo-name="Opening hours - closes" value="17:00">
                                </div>
                                <div class="grow mt-4 md:mt-0 md:text-right">
                                    <a href="#" onclick="return deleteMatrixRow('openingHours', '${rowId}')" class="text-sm underline">Remove these hours</a>
                                </div>
                            </div>
                        </div>
                    </div>`;
            }
        });

        // Closure dates. Unlike the other two, having none is valid, so the last row
        // stays removable.
        matrixRepeater({
            field: 'holidayDates',
            container: 'closures-container',
            order: 'closures-order',
            addButton: 'button-add-closure',
            keepOne: false,
            rowHtml: function(rowId) {
                return `
                    <div data-row-id="${rowId}">
                        <input type="hidden" name="fields[holidayDates][entries][${rowId}][type]" value="holidayDatesBlock">
                        <div class="md:flex items-center justify-between gap-10 border border-gray-300 p-5 rounded-lg">
                            <div class="md:flex items-center gap-8 w-full">
                                <div>
                                    <label for="fields-holidayDates-entries-${rowId}-date" class="block mb-1 font-semibold text-mine-shaft">Closed on <span class="italic text-pink font-light">(Required)</span></label>
                                    <input type="date" required id="fields-holidayDates-entries-${rowId}-date" class="bg-gray-50 w-44 md:w-34 lg:w-44 border border-gray-300 text-mine-shaft rounded-lg focus:ring-primary-600 focus:border-primary-600 block p-2.5" name="fields[holidayDates][entries][${rowId}][fields][date]" data-matomo-name="Closure date" value="">
                                </div>
                                <div class="grow mt-4 md:mt-0 md:text-right">
                                    <a href="#" onclick="return deleteMatrixRow('holidayDates', '${rowId}')" class="text-sm underline">Remove closure</a>
                                </div>
                            </div>
                        </div>
                    </div>`;
            }
        });
    });

    //
    // Form validation
    //
    section('form validation', function () {
        var formElm = document.querySelector(".form-validate");

        if(formElm) {

            var config = {
                // class of the parent element where the error/success class is added
                classTo: 'field-group',
                errorClass: 'validate-error',
                successClass: 'validate-success',
                // class of the parent element where error text element is appended
                errorTextParent: 'field-group',
                // type of element to create for the error text
                errorTextTag: 'div',
                // class of the error text element
                errorTextClass: 'validate-help' 
            };

            // create the pristine instance
            var pristine = new Pristine(formElm, config);

            // Only allow form submission if valid
            formElm.addEventListener('submit', function(e) {
                e.preventDefault();

                // Get array of "active" fields (not hidden by conditional logic).
                //
                // We test the field's CONTAINER, not the field itself. Styled radios
                // and checkboxes are deliberately display:none with a visible <label>
                // standing in for them, so testing the input directly marked them
                // inactive and skipped their validation entirely — which is how an
                // event could be submitted with no location at all (#226).
                var activeFields = Array.from(formElm.elements);
                    activeFields = activeFields.filter(function(el) {
                        if (el.tagName === "BUTTON" || el.tagName === "FIELDSET") {
                            return false;
                        }

                        var container = el.parentElement;

                        return container !== null && container.offsetParent !== null;
                    });

                // Only validate active fields
                var valid = pristine.validate(activeFields);

                if (valid) {
                    formElm.submit();
                }
            });

        }
    });

    //
    // Pageview chart
    //
    section('pageview chart', function () {
        var pageviewsChartElm = document.getElementById("pageviews-chart");
    
        if(pageviewsChartElm) {

            var pageviewOptions = {
                chart: {
                    height: "100%",
                    maxWidth: "100%",
                    type: "area",
                    fontFamily: "Popins, sans-serif",
                    zoom: {
                        enabled: false
                    },
                    dropShadow: {
                        enabled: false,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                tooltip: {
                    enabled: true,
                    x: {
                        show: false,
                    },
                },
                fill: {
                    type: "gradient",
                    gradient: {
                        opacityFrom: 0.55,
                        opacityTo: 0,
                        shade: "#D3145A",
                        gradientToColors: ["#D3145A"],
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    width: 6,
                    colors: ["#D3145A"]
                },
                grid: {
                    show: false,
                    strokeDashArray: 4,
                    padding: {
                    left: 2,
                    right: 2,
                    top: 0
                    },
                },
                series: [],
                xaxis: {
                    type: 'datetime',
                    labels: {
                    show: true,
                    format: 'd MMM',
                    },
                    axisBorder: {
                    show: false,
                    },
                    axisTicks: {
                    show: false,
                    },
                },
                yaxis: {
                    show: false,
                },
                noData: {
                    text: 'Loading...'
                }
            }

            // Init chart
            var pageviewsChart = new ApexCharts(pageviewsChartElm, pageviewOptions);
            pageviewsChart.render();
        
            // Populate chart
            var eventId = pageviewsChartElm.getAttribute("data-event-id");
            var period = pageviewsChartElm.getAttribute("data-period");

            axios({
                method: 'GET',
                url: "/actions/_event-analytics/reporting/pageviews?entryId=" + eventId + "&period=" + period,
                }).then(function(response) {
                
                    var total = response.data.totalPageviews ?? 0;
                    var period = response.data.dataPeriod ?? 7; 

                    document.getElementById("chart-total").innerHTML = total;
                    document.getElementById("period-days").innerHTML = period;

                    if(response.data.data != undefined) {
                        pageviewsChart.updateSeries([{
                            name: 'Pageviews',
                            data: response.data.data,
                            color: '#D3145A'
                        }])
                    }
            });

        }
    });

    //
    // CK Editor init
    //
    section('ckeditor', function () {
        var ckEditorsElms = document.querySelectorAll('.richEditor');
    
        if(ckEditorsElms) {
        
            ckEditorsElms.forEach((el) => {
            ClassicEditor
                .create(el, {
                    // Required from CKEditor 5 v44 onwards. 'GPL' selects the same
                    // open-source terms the editor was already used under before v44
                    // made the choice explicit, so this changed nothing.
                    //
                    // Deliberate and reviewed — see #258. WOHB is self-hosted and not
                    // distributed, which is the case CKEditor's GPL terms are written
                    // for. Don't swap this for a commercial key without a reason to.
                    licenseKey: 'GPL',
                    plugins: [ Essentials, Bold, Italic, Paragraph, Heading, HeadingButtonsUI, ParagraphButtonUI, Link, ListCK ],
                    toolbar: {
                        items: [
                            'undo', 'redo', '|', 'heading2', 'paragraph', 'link', 'bold', 'italic', '|', 'numberedList', 'bulletedList'
                        ]
                    },
                    heading: {
                        options: [
                            { model: 'heading2', view: 'h2', title: 'Heading', class: 'ck-heading_heading2' },
                            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                        ]
                    }
                });
            });

        }
    });

    //
    // Form conditional rules
    //
    section('conditional rules', function () {
        var conditionalRuleElms = document.querySelectorAll(".form-conditional-rules");

        if(conditionalRuleElms) {

            let initialized = [];
            conditionalRuleElms.forEach((element) => {
                let form = element.closest('form');
                let formId = form.getAttribute('id');

                if (initialized.includes(formId)) {
                    return;
                }

                initialized.push(formId);

                mfConditionalFields('#' + formId, {
                    debug: process.env.NODE_ENV === 'development',
                    rules: 'block',
                });
            });

        }
    });

    //
    // Copy to clipboard
    //
    section('copy to clipboard', function () {
        var clipboardButton = document.getElementById('clipboard-button');

        // Add date button listerner
        if(clipboardButton !== null) {
            clipboardButton.addEventListener("click", function(e) {
                e.preventDefault();
            
                var clipboardText = document.getElementById('clipboard-text');
                var $defaultMessage = document.getElementById('default-message');
                var $successMessage = document.getElementById('success-message');

                // Select the text field
                clipboardText.select();
                clipboardText.setSelectionRange(0, 99999); // For mobile devices

                // Copy the text inside the text field
                navigator.clipboard.writeText(clipboardText.value);

                $defaultMessage.classList.add('hidden');
                $successMessage.classList.remove('hidden');

                // reset to default state
                setTimeout(() => {
                    $defaultMessage.classList.remove('hidden');
                    $successMessage.classList.add('hidden');
                }, 2000);

            });
        }
    });

});